/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { entityNavigatedTopic } from '@agilelab/plugin-wb-events-common';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import { Progress } from '@mainstage/core-components';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  AsyncEntityProvider,
  useAsyncEntity,
} from '@mainstage/plugin-catalog-react';
import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';
import { strCmpAll } from '../EntitySwitch/conditions';
import { useEntityFromUrl } from './useEntityFromUrl';

function CatalogEntityPageContent() {
  const configApi = useApi(configApiRef);
  const hiddenKinds = configApi.getOptionalStringArray('catalog.hiddenKinds');
  const { entity, loading } = useAsyncEntity();
  const { publish } = usePublishEvent({
    generatorSystem: 'builder',
  });

  /**
   * publish event only on components and systems
   */
  useEffect(() => {
    if (
      entity &&
      (entity.kind.toLowerCase() === 'system' ||
        entity.kind.toLowerCase() === 'component')
    ) {
      publish(entityNavigatedTopic, {
        entityRef: stringifyEntityRef(entity),
      });
    }
  }, [entity, publish]);

  if (loading) return <Progress />;

  if (strCmpAll(entity?.kind, hiddenKinds || [])) return <Navigate to="/" />;

  return <Outlet />;
}

/** @public */
export function CatalogEntityPage() {
  return (
    <AsyncEntityProvider {...useEntityFromUrl()}>
      <CatalogEntityPageContent />
    </AsyncEntityProvider>
  );
}
